import type { TaskWithState, TodayView } from '../../shared/types'
import { approvalLabel, tickState, type TickState } from '../../shared/domain/approval'
import { Avatar, Check, Pill } from './ui'

/**
 * One kid's day as a parent sees it: what's on their plate and where each item
 * stands. Ticking things off belongs on the kid's own screen, so `onOpen` jumps
 * there; the one action here is approving what the kid says they've done.
 */
export function KidStatusCard({
  day,
  onOpen,
  onApprove,
  busy = false,
  viewerId = null,
}: {
  day: TodayView
  onOpen: () => void
  /** The parent looking, so their own approvals read "Approved by you". */
  viewerId?: string | null
  /** Approves a waiting tick, shared jobs included. */
  onApprove: (completionId: string) => void
  busy?: boolean
}) {
  const { member } = day
  const toGo = day.ready.gates.filter((gate) => !gate.done).length
  // A sent-back household job has no claim any more, but is still the one they picked.
  const contribution =
    day.householdDaily.find((task) => task.claimedBy?.memberId === member.id) ??
    day.householdDaily.find((task) => task.sentBack)

  return (
    <section className="card">
      <div className="row">
        <Avatar emoji={member.emoji} color={member.color} ring />
        <div className="grow row-between">
          <h2 className="title">{member.name}</h2>
          {day.ready.ready ? <Pill tone="ok">Ready</Pill> : <Pill tone="warn">{toGo} to go</Pill>}
        </div>
      </div>

      <h3 className="label">Chores</h3>
      {day.personalDaily.length === 0 ? (
        <p className="faint">No chores assigned.</p>
      ) : (
        <div className="list">
          {day.personalDaily.map((task) => (
            <StatusRow
              key={task.id}
              task={task}
              kidName={member.name}
              viewerId={viewerId}
              busy={busy}
              onApprove={onApprove}
            />
          ))}
        </div>
      )}

      <h3 className="label">Household contribution</h3>
      {contribution ? (
        <StatusRow task={contribution} kidName={member.name} viewerId={viewerId} busy={busy} onApprove={onApprove} />
      ) : (
        <p className="faint">Not picked yet.</p>
      )}

      <button type="button" className="btn btn-quiet" onClick={onOpen}>
        Open {member.name}’s day
      </button>
    </section>
  )
}

function StatusRow({
  task,
  kidName,
  viewerId,
  busy,
  onApprove,
}: {
  task: TaskWithState
  kidName: string
  viewerId: string | null
  busy: boolean
  onApprove: (completionId: string) => void
}) {
  const state: TickState = tickState(task)
  const className = `check${state === 'done' ? ' check-done' : state === 'waiting' ? ' check-waiting' : ''}`
  const body = (
    <>
      <Check done={state !== 'open'} />
      <span className="check-body">
        <span className="check-title">{task.title}</span>
        {state === 'waiting' ? <span className="check-waiting-text">Waiting for you — tap to approve</span> : null}
        {state === 'done' ? <span className="check-detail">{approvalLabel(task.approvedBy, viewerId)}</span> : null}
        {state === 'open' && task.sentBack ? (
          <span className="check-waiting-text">Sent back, not redone yet</span>
        ) : null}
      </span>
    </>
  )

  const completionId = task.completionId
  if (state !== 'waiting' || !completionId) return <div className={className}>{body}</div>

  return (
    <button
      type="button"
      className={className}
      disabled={busy}
      aria-label={`Approve ${kidName}’s ${task.title}`}
      onClick={() => onApprove(completionId)}
    >
      {body}
    </button>
  )
}
